import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  useTheme,
  Stack,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Search,
  LocationOn,
  FilterList,
  Clear,
  Psychology,
  LocalHospital,
  School,
  TrendingUp,
  Help,
  Refresh,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectSearchSuggestions } from '../../features/homepage/homepageSlice';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
  borderRadius: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4),
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 100,
  height: 100,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  '& svg': {
    fontSize: 48,
    color: theme.palette.primary.main,
  },
}));

const SuggestionChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
}));

const NoResults = ({
  searchQuery,
  onReset,
  onSuggestionClick,
  suggestions = [],
  showSuggestions = true,
}) => {
  const theme = useTheme();
  
  // Get search suggestions from Redux store
  const backendSuggestions = useSelector(selectSearchSuggestions);

  // Helper function to get icon based on suggestion type
  const getSuggestionIcon = (suggestionType) => {
    const iconMap = {
      'specialty': LocalHospital,
      'condition': TrendingUp,
      'location': LocationOn,
      'doctor_name': Psychology,
      'popular': Search,
    };
    return iconMap[suggestionType] || Search;
  };

  // Transform backend suggestions to match component format
  const transformedSuggestions = backendSuggestions.map(suggestion => ({
    text: suggestion.text,
    icon: getSuggestionIcon(suggestion.suggestion_type),
    category: suggestion.suggestion_type.charAt(0).toUpperCase() + suggestion.suggestion_type.slice(1),
    type: suggestion.suggestion_type,
  }));

  // Default suggestions if no backend data available
  const defaultSuggestions = [
    { text: 'Psychiatrist', icon: LocalHospital, category: 'Specialty' },
    { text: 'Psychologist', icon: Psychology, category: 'Specialty' },
    { text: 'Counselor', icon: School, category: 'Specialty' },
    { text: 'Anxiety', icon: TrendingUp, category: 'Condition' },
    { text: 'Depression', icon: TrendingUp, category: 'Condition' },
    { text: 'Stress Management', icon: TrendingUp, category: 'Condition' },
  ];

  const finalSuggestions = transformedSuggestions.length > 0 ? transformedSuggestions : defaultSuggestions;

  const handleSuggestionClick = (suggestion) => {
    if (onSuggestionClick) {
      onSuggestionClick(suggestion.text);
    }
  };

  const getSearchTips = () => [
    'Try using broader search terms',
    'Check your spelling',
    'Remove some filters to see more results',
    'Try searching by specialty instead of condition',
    'Expand your location radius',
  ];

  return (
    <StyledPaper elevation={0}>
      <IconWrapper>
        <Search />
      </IconWrapper>

      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
        No doctors found
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
        {searchQuery
          ? `We couldn't find any doctors matching "${searchQuery}"`
          : "We couldn't find any doctors matching your criteria"}
      </Typography>

      {/* Search Tips */}
      <Box sx={{ mb: 4, textAlign: 'left', maxWidth: 500, mx: 'auto' }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Help sx={{ fontSize: 20 }} />
          Search Tips
        </Typography>
        <List dense>
          {getSearchTips().map((tip, index) => (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={tip}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.secondary'
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Popular Searches */}
      {showSuggestions && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Try these popular searches
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
            {finalSuggestions.map((suggestion, index) => (
              <SuggestionChip
                key={index}
                label={suggestion.text}
                icon={<suggestion.icon />}
                variant="outlined"
                color="primary"
                onClick={() => handleSuggestionClick(suggestion)}
                sx={{
                  '& .MuiChip-icon': {
                    fontSize: 16,
                  },
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
        <Button
          variant="contained"
          startIcon={<Refresh />}
          onClick={onReset}
          sx={{
            borderRadius: theme.shape.borderRadius * 2,
            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            px: 3,
            py: 1.5,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
            },
          }}
        >
          Reset Filters
        </Button>

        <Button
          variant="outlined"
          startIcon={<FilterList />}
          onClick={() => {
            // Scroll to filters section
            const filterSection = document.querySelector('[data-testid="filter-panel"]');
            if (filterSection) {
              filterSection.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          sx={{
            borderRadius: theme.shape.borderRadius * 2,
            px: 3,
            py: 1.5,
          }}
        >
          Adjust Filters
        </Button>
      </Stack>

      {/* Additional Help */}
      <Box sx={{ mt: 4, p: 3, bgcolor: theme.palette.grey[50], borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Still having trouble finding what you're looking for?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Contact our support team for personalized assistance in finding the right mental health professional for you.
        </Typography>
      </Box>
    </StyledPaper>
  );
};

export default NoResults;
